import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIncoming, PhoneOutgoing, ArrowLeft, Phone, Settings, Play, Pause, CheckCircle2, Send, Search, CloudUpload, Shield, X, ChevronRight, Users, Trash2, Bot, StickyNote, User, Clock3, FileText, CalendarDays, Siren } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { CallsEmptyState } from '../components/calls/CallsEmptyState';
interface CallsScreenProps {
  onNavigate: (screen: string) => void;
  onDetailView?: (isOpen: boolean) => void;
  simulatedCall?: any;
  initialCallId?: number | null;
  onInitialCallOpened?: () => void;
  onViewBookedEvent?: (eventId: number) => void;
}
interface Message {
  sender: 'user' | 'ai';
  text: string;
  time: string;
  /**
   * Marks a private side-channel exchange between the account owner and their
   * AI (a command and its acknowledgement) — NOT part of the spoken call.
   * Rendered distinctly from the caller/AI conversation.
   */
  internal?: boolean;
}
interface Call {
  id: number;
  name: string;
  type: 'live' | 'incoming' | 'outgoing';
  time: string;
  duration: string;
  initials: string;
  color: string;
  group: string;
  summary: string;
  messages: Message[];
  booking?: {
    eventId: number;
    title: string;
    time: string;
  };
  isLive?: boolean;
  isFirstCall?: boolean;
  /** Flagged by the AI when the caller reports an urgent situation. */
  isEmergency?: boolean;
}
export function CallsScreen({
  onNavigate,
  onDetailView,
  simulatedCall,
  initialCallId,
  onInitialCallOpened,
  onViewBookedEvent
}: CallsScreenProps) {
  const {
    isLight
  } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [selectedCaller, setSelectedCaller] = useState<string | null>(null);
  const [callerOverviewCall, setCallerOverviewCall] = useState<Call | null>(null);
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  const [commandInput, setCommandInput] = useState('');
  const detailScrollRef = useRef<HTMLDivElement>(null);
  const [liveTyping, setLiveTyping] = useState(false);
  const liveSimTimerRef = useRef<NodeJS.Timeout | null>(null);
  const liveSimActiveRef = useRef(false);
  // Backup prompt state
  const [backupPromptShown, setBackupPromptShown] = useState(false);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const [backupStep, setBackupStep] = useState<'ask' | 'provider' | 'frequency' | 'done'>('ask');
  const [backupFrequency, setBackupFrequency] = useState<string | null>(null);
  const [backupProvider, setBackupProvider] = useState<string | null>(null);
  // Call confirmation state
  const [showCallConfirm, setShowCallConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletedCallIds, setDeletedCallIds] = useState<number[]>([]);
  // A brand-new account has no call history until Vexu answers its first call.
  const [callsSeeded, setCallsSeeded] = useState(false);
  // Once a simulated live call wraps up it becomes a normal log entry.
  const [completedLiveCall, setCompletedLiveCall] = useState<Call | null>(null);
  // What was already said before you opened the emergency call — the transcript
  // opens mid-conversation so you can catch up instantly.
  const emergencyCallPreamble: Message[] = [{
    sender: 'ai',
    text: "Hello, you've reached Fadi's assistant. How can I help you?",
    time: '2:30 PM'
  }, {
    sender: 'user',
    text: "This is urgent — there's a water leak flooding the server room at the Harbour office. I need Fadi now.",
    time: '2:30 PM'
  }, {
    sender: 'ai',
    text: 'Understood. I’m treating this as an emergency. Is anyone in immediate danger?',
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: 'No one is hurt, but the racks are still powered and water is rising.',
    time: '2:31 PM'
  }];
  // The part that streams in live while you watch.
  const emergencyCallScript: Message[] = [{
    sender: 'ai',
    text: 'Hold on, please stay on the line — I’m trying to reach Fadi right now.',
    time: '2:31 PM'
  }, {
    sender: 'ai',
    text: 'I’ve just sent Fadi a notification flagged as an urgent emergency.',
    time: '2:32 PM'
  }, {
    sender: 'user',
    text: 'Please hurry, the water is getting close to the power units.',
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: 'I’m still trying to reach Fadi — staying on it. Please move away from the racks meanwhile.',
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: 'Fadi just replied — he’ll call you back in 5 minutes. Please keep this line free.',
    time: '2:33 PM'
  }];
  // Full conversation script for the live call simulation
  const liveCallScript: Message[] = [{
    sender: 'ai',
    text: "Hello, you've reached the assistant. How can I help you?",
    time: '2:30 PM'
  }, {
    sender: 'user',
    text: 'Hi, is Sarah available? I need to discuss the project deadline.',
    time: '2:30 PM'
  }, {
    sender: 'ai',
    text: "She's currently unavailable. I can take a message or schedule a callback.",
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: "Please tell her to call me back, it's about the Q4 report.",
    time: '2:31 PM'
  }, {
    sender: 'ai',
    text: "Of course. I'll mark this as high priority. Can I get your name?",
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: "It's James from the finance team.",
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: "Got it, James. I'll let Sarah know right away. Is there anything else?",
    time: '2:32 PM'
  }, {
    sender: 'user',
    text: "No, that's all. Thanks!",
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: "You're welcome. Have a great day!",
    time: '2:32 PM'
  }];
  // Notify parent when a caller overview or transcript opens/closes.
  useEffect(() => {
    onDetailView?.(selectedCall !== null || selectedCaller !== null);
  }, [selectedCall, selectedCaller]);
  // Live call simulation: deliver messages one by one using recursive setTimeout
  useEffect(() => {
    if (!selectedCall?.isLive) {
      // Clean up if we leave the live call
      liveSimActiveRef.current = false;
      if (liveSimTimerRef.current) {
        clearTimeout(liveSimTimerRef.current);
        liveSimTimerRef.current = null;
      }
      return;
    }
    // Prevent double-start (React StrictMode)
    if (liveSimActiveRef.current) return;
    liveSimActiveRef.current = true;
    const isEmergencyCall = Boolean(selectedCall.isEmergency);
    const activeScript = isEmergencyCall ? emergencyCallScript : liveCallScript;
    // An emergency opens mid-call: show what was already said, then stream on.
    setSelectedCall(prev => prev ? {
      ...prev,
      messages: isEmergencyCall ? emergencyCallPreamble : []
    } : prev);
    setLiveTyping(false);
    let currentIndex = 0;
    const deliverNextMessage = () => {
      if (!liveSimActiveRef.current) return;
      if (currentIndex >= activeScript.length) {
        // All messages delivered — end the call
        setLiveTyping(false);
        liveSimTimerRef.current = setTimeout(() => {
          if (!liveSimActiveRef.current) return;
          setSelectedCall(prev => {
            if (!prev) return prev;
            const endedCall: Call = {
              ...prev,
              type: 'incoming',
              isLive: false,
              duration: isEmergencyCall ? '3m 08s' : '2m 32s',
              time: isEmergencyCall ? '2:33 PM' : '2:32 PM',
              summary: isEmergencyCall ? 'Emergency reported by Michael Torres (facilities): water leak flooding the Harbour office server room. No injuries. Vexu kept the caller on the line, sent Fadi an urgent emergency alert, and confirmed Fadi will call back within 5 minutes.' : 'James from finance called regarding the Q4 report deadline. Requested a callback from Sarah. Marked as high priority.'
            };
            // Keep the finished call in the log instead of a stale "live" row.
            setCompletedLiveCall(endedCall);
            return endedCall;
          });
          liveSimActiveRef.current = false;
          // Scroll to summary
          setTimeout(() => {
            detailScrollRef.current?.scrollTo({
              top: detailScrollRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }, 500);
        }, 1500);
        return;
      }
      const typingDelay = currentIndex === 0 ? 800 : 1200;
      const messageDelay = activeScript[currentIndex].sender === 'ai' ? 1800 : 1400;
      // Show typing indicator
      liveSimTimerRef.current = setTimeout(() => {
        if (!liveSimActiveRef.current) return;
        setLiveTyping(true);
        // Deliver the message
        liveSimTimerRef.current = setTimeout(() => {
          if (!liveSimActiveRef.current) return;
          setLiveTyping(false);
          const msg = activeScript[currentIndex];
          setSelectedCall(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...prev.messages, msg]
            };
          });
          // Auto-scroll
          setTimeout(() => {
            detailScrollRef.current?.scrollTo({
              top: detailScrollRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }, 100);
          currentIndex++;
          // Schedule next message
          deliverNextMessage();
        }, messageDelay);
      }, typingDelay);
    };
    // Start the simulation after a brief initial delay
    liveSimTimerRef.current = setTimeout(() => {
      deliverNextMessage();
    }, 600);
    return () => {
      liveSimActiveRef.current = false;
      if (liveSimTimerRef.current) {
        clearTimeout(liveSimTimerRef.current);
        liveSimTimerRef.current = null;
      }
    };
  }, [selectedCall?.isLive, selectedCall?.id]);
  // Auto-scroll for non-live calls
  useEffect(() => {
    if (selectedCall && !selectedCall.isLive && detailScrollRef.current) {
      setTimeout(() => {
        detailScrollRef.current?.scrollTo({
          top: detailScrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 400);
    }
  }, [selectedCall]);
  const baseCalls: Call[] = [{
    id: 1,
    name: 'Sarah Miller',
    type: 'live',
    time: 'Now',
    duration: '1m 42s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    isLive: true,
    summary: '',
    messages: []
  }, {
    id: 12,
    name: 'Nadia Haddad',
    type: 'incoming',
    time: '12:04 PM',
    duration: '1m 58s',
    initials: 'NH',
    color: isLight ? 'bg-red-100 text-red-700' : 'bg-red-500/20 text-red-400',
    group: 'Emergency',
    isEmergency: true,
    summary: 'Nadia Haddad reported a gas smell in the ground-floor unit at Marina Heights. No one injured, building being evacuated. Vexu flagged the call as an emergency and escalated it to you for an immediate callback.',
    messages: [{
      sender: 'ai',
      text: "Hello, you've reached the assistant. How can I help you?",
      time: '12:04 PM'
    }, {
      sender: 'user',
      text: 'There is a strong gas smell in the ground-floor unit at Marina Heights. We need someone now.',
      time: '12:04 PM'
    }, {
      sender: 'ai',
      text: 'I’m treating this as an emergency. Is anyone injured or still inside the unit?',
      time: '12:05 PM'
    }, {
      sender: 'user',
      text: 'No injuries. We are evacuating the floor right now.',
      time: '12:05 PM'
    }, {
      sender: 'ai',
      text: 'Understood. Keep everyone outside and away from the entrance. I’m alerting the team and sharing your number for an immediate callback.',
      time: '12:06 PM'
    }]
  }, {
    id: 2,
    name: 'Mike Ross',
    type: 'incoming',
    time: '11:15 AM',
    duration: '2m 14s',
    initials: 'MR',
    color: isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-100/60 text-blue-600',
    group: 'VIP Clients',
    summary: 'Caller scheduled a meeting for tomorrow at 2 PM. Calendar booking confirmed.',
    booking: {
      eventId: 2,
      title: 'Client Discovery Call',
      time: '1:30 PM – 2:15 PM'
    },
    messages: [{
      sender: 'ai',
      text: 'Thank you for calling. How may I assist you?',
      time: '11:15 AM'
    }, {
      sender: 'user',
      text: "I'd like to schedule a meeting for tomorrow afternoon.",
      time: '11:15 AM'
    }, {
      sender: 'ai',
      text: 'I can check the calendar. What time works best for you?',
      time: '11:16 AM'
    }, {
      sender: 'user',
      text: 'Around 2 PM if possible.',
      time: '11:16 AM'
    }, {
      sender: 'ai',
      text: "Let me check... 2 PM is available. I'll book that for you.",
      time: '11:16 AM'
    }, {
      sender: 'user',
      text: 'Perfect, thanks!',
      time: '11:17 AM'
    }]
  }, {
    id: 3,
    name: '+1 (555) 012-3456',
    type: 'outgoing',
    time: 'Yesterday',
    duration: '45s',
    initials: '#',
    color: isLight ? 'bg-slate-200 text-slate-700' : 'bg-slate-100/60 text-slate-600',
    group: 'All Contacts',
    summary: 'Brief call — wrong number. No action required.',
    messages: [{
      sender: 'ai',
      text: "Hello, you've reached the assistant.",
      time: '4:12 PM'
    }, {
      sender: 'user',
      text: 'Wrong number, sorry.',
      time: '4:12 PM'
    }, {
      sender: 'ai',
      text: 'No problem at all. Have a great day!',
      time: '4:12 PM'
    }]
  }, {
    id: 4,
    name: 'Design Team',
    type: 'incoming',
    time: 'Mon',
    duration: '5m 30s',
    initials: 'DT',
    color: isLight ? 'bg-vexu/20 text-vexu-700' : 'bg-emerald-100/60 text-emerald-600',
    group: 'Work Contacts',
    summary: 'Urgent request for design mockup approval before EOD. Priority notification sent. Client presentation scheduled for tomorrow.',
    messages: [{
      sender: 'ai',
      text: 'Thank you for calling. They are currently unavailable.',
      time: '3:45 PM'
    }, {
      sender: 'user',
      text: 'This is urgent — we need approval on the design mockups before EOD.',
      time: '3:45 PM'
    }, {
      sender: 'ai',
      text: 'I understand this is urgent. Let me try to reach them right away.',
      time: '3:46 PM'
    }, {
      sender: 'user',
      text: 'Please do. The client presentation is tomorrow morning.',
      time: '3:46 PM'
    }, {
      sender: 'ai',
      text: "I've sent a priority notification. They should get back to you shortly.",
      time: '3:47 PM'
    }, {
      sender: 'user',
      text: "Great, I'll wait for their call.",
      time: '3:47 PM'
    }]
  }, {
    id: 5,
    name: 'Mom',
    type: 'incoming',
    time: 'Sun',
    duration: '1m 12s',
    initials: 'M',
    color: isLight ? 'bg-pink-100 text-pink-700' : 'bg-pink-100/60 text-pink-600',
    group: 'Family',
    isFirstCall: true,
    summary: 'First call from Mom. Requested a callback when available. No urgency.',
    messages: [{
      sender: 'ai',
      text: "Hi! They're not available right now but I can take a message.",
      time: '6:20 PM'
    }, {
      sender: 'user',
      text: "Just tell them to call me when they're free. Nothing urgent.",
      time: '6:20 PM'
    }, {
      sender: 'ai',
      text: "Got it! I'll let them know. Anything else?",
      time: '6:21 PM'
    }]
  }, {
    id: 6,
    name: 'Sarah Miller',
    type: 'incoming',
    time: 'Yesterday · 4:20 PM',
    duration: '3m 08s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    summary: 'Sarah checked in on the product launch timeline and asked for a callback after the client review.',
    messages: [{
      sender: 'ai',
      text: 'Hi, you have reached the assistant. How can I help?',
      time: '4:20 PM'
    }, {
      sender: 'user',
      text: 'Could you ask them to call me after the client review? I want to check the launch timeline.',
      time: '4:21 PM'
    }, {
      sender: 'ai',
      text: 'Absolutely. I will pass that along.',
      time: '4:22 PM'
    }]
  }, {
    id: 7,
    name: 'Sarah Miller',
    type: 'incoming',
    time: 'Mon · 10:05 AM',
    duration: '1m 46s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    summary: 'Sarah shared revised presentation notes and requested confirmation before tomorrow morning.',
    messages: [{
      sender: 'ai',
      text: 'Thanks for calling. How may I assist?',
      time: '10:05 AM'
    }, {
      sender: 'user',
      text: 'I have revised presentation notes. Please ask them to confirm they received them.',
      time: '10:06 AM'
    }, {
      sender: 'ai',
      text: 'I will make sure they receive that message.',
      time: '10:06 AM'
    }]
  }, {
    id: 8,
    name: 'Sarah Miller',
    type: 'incoming',
    time: 'Fri · 2:40 PM',
    duration: '2m 21s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    summary: 'Sarah asked about availability for a planning session next week. No booking was made.',
    messages: [{
      sender: 'ai',
      text: 'Hello, how can I help today?',
      time: '2:40 PM'
    }, {
      sender: 'user',
      text: 'I am trying to find a time for a planning session next week.',
      time: '2:41 PM'
    }, {
      sender: 'ai',
      text: 'I will let them know and ask them to follow up with availability.',
      time: '2:42 PM'
    }]
  }, {
    id: 9,
    name: 'Mike Ross',
    type: 'incoming',
    time: 'Yesterday · 9:30 AM',
    duration: '1m 38s',
    initials: 'MR',
    color: isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-100/60 text-blue-600',
    group: 'VIP Clients',
    summary: 'Mike asked for the latest proposal version before sharing it with his team.',
    messages: [{
      sender: 'ai',
      text: 'Hello, how can I help today?',
      time: '9:30 AM'
    }, {
      sender: 'user',
      text: 'Could you ask them to send over the latest proposal?',
      time: '9:31 AM'
    }, {
      sender: 'ai',
      text: 'I will pass that request along.',
      time: '9:31 AM'
    }]
  }, {
    id: 10,
    name: 'Mike Ross',
    type: 'incoming',
    time: 'Tue · 3:15 PM',
    duration: '2m 05s',
    initials: 'MR',
    color: isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-100/60 text-blue-600',
    group: 'VIP Clients',
    summary: 'Mike requested a callback to review contract feedback before the end of the week.',
    messages: [{
      sender: 'ai',
      text: 'Thanks for calling. What can I help with?',
      time: '3:15 PM'
    }, {
      sender: 'user',
      text: 'I have contract feedback to review this week.',
      time: '3:16 PM'
    }, {
      sender: 'ai',
      text: 'I will make sure they receive your message.',
      time: '3:17 PM'
    }]
  }, {
    id: 11,
    name: 'Design Team',
    type: 'incoming',
    time: 'Yesterday · 1:10 PM',
    duration: '2m 48s',
    initials: 'DT',
    color: isLight ? 'bg-vexu/20 text-vexu-700' : 'bg-emerald-100/60 text-emerald-600',
    group: 'Work Contacts',
    summary: 'The team requested feedback on the onboarding illustrations and noted a Thursday delivery target.',
    messages: [{
      sender: 'ai',
      text: 'Hello, how can I help?',
      time: '1:10 PM'
    }, {
      sender: 'user',
      text: 'We need feedback on the onboarding illustrations by Thursday.',
      time: '1:11 PM'
    }, {
      sender: 'ai',
      text: 'I will share that priority with them.',
      time: '1:12 PM'
    }]
  }];
  const historyCalls: Call[] = callsSeeded ? baseCalls : [];
  const calls: Call[] = simulatedCall ? [completedLiveCall ?? {
    id: 999,
    name: simulatedCall.name,
    type: 'live',
    time: 'Now',
    duration: '0:00',
    initials: simulatedCall.initials,
    color: isLight ? simulatedCall.colorLight : simulatedCall.colorDark,
    group: simulatedCall.group,
    isLive: true,
    isEmergency: Boolean(simulatedCall.isEmergency),
    summary: '',
    messages: []
  }, ...historyCalls] : historyCalls;
  const visibleCalls = calls.filter(c => !deletedCallIds.includes(c.id));
  const showEmptyState = !callsSeeded && !simulatedCall;
  // A live call or a deep link from elsewhere implies real history exists.
  useEffect(() => {
    if (simulatedCall && !callsSeeded) setCallsSeeded(true);
  }, [simulatedCall, callsSeeded]);
  // A fresh simulated call replaces any previously completed one.
  useEffect(() => {
    setCompletedLiveCall(null);
  }, [simulatedCall]);
  useEffect(() => {
    if (initialCallId === null || initialCallId === undefined) return;
    if (!callsSeeded) {
      setCallsSeeded(true);
      return;
    }
    const referencedCall = calls.find(call => call.id === initialCallId);
    if (referencedCall) {
      setSelectedCaller(null);
      setSelectedCall(referencedCall);
      setPlayingIdx(null);
      setCommandInput('');
      setLiveTyping(false);
    }
    onInitialCallOpened?.();
  }, [initialCallId, calls, onInitialCallOpened]);
  const filteredCalls = searchQuery.trim() ? visibleCalls.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.group.toLowerCase().includes(searchQuery.toLowerCase())) : visibleCalls;
  const callerHistoryCalls = selectedCaller ? visibleCalls.filter(call => call.name === selectedCaller && !call.isLive && call.id !== callerOverviewCall?.id) : [];
  const callerProfile = callerOverviewCall ?? callerHistoryCalls[0];
  const getCallPhoneNumber = (call: Call) => {
    const numbers: Record<number, string> = {
      1: '+1 (415) 555-0182',
      2: '+1 (646) 555-0134',
      4: '+1 (212) 555-0168',
      5: '+1 (415) 555-0101',
      6: '+1 (415) 555-0182',
      7: '+1 (628) 555-0147',
      8: '+1 (415) 555-0182',
      9: '+1 (646) 555-0134',
      10: '+1 (917) 555-0196',
      11: '+1 (212) 555-0168'
    };
    return numbers[call.id] ?? '+1 (555) 012-3456';
  };
  const selectedCallHistoryCount = selectedCall ? visibleCalls.filter(call => call.name === selectedCall.name && !call.isLive && call.id !== selectedCall.id).length : 0;
  const handleDeleteCall = () => {
    if (!selectedCall) return;
    const remainingCallerCalls = visibleCalls.filter(call => call.name === selectedCall.name && call.id !== selectedCall.id && !call.isLive);
    setDeletedCallIds(prev => [...prev, selectedCall.id]);
    setShowDeleteConfirm(false);
    setSelectedCall(null);
    if (selectedCaller && remainingCallerCalls.length === 0) {
      setSelectedCaller(null);
    }
  };
  const togglePlay = (idx: number) => {
    if (playingIdx === idx) {
      setPlayingIdx(null);
    } else {
      setPlayingIdx(idx);
      setTimeout(() => setPlayingIdx(null), 2000);
    }
  };
  // Renders a private owner↔Vexu command message using the SAME bubble
  // structure as the call conversation (avatar + name label + aligned bubble +
  // time), but in a distinct amber "private" color so it reads as internal.
  // No play button — commands are typed, not spoken/recorded.
  const renderInternalBubble = (m: Message, key: React.Key) => {
    const isCommand = m.sender === 'user';
    return <motion.div key={key} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.2
    }} className={`flex items-end gap-2 pt-1 ${isCommand ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-2199" data-mp="y35csy0t8dq7">
        {/* Avatar */}
        <div className="w-7 flex-shrink-0 self-end" data-id="element-2200" data-mp="9jv9ge69yvhk">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isLight ? 'bg-amber-100 text-amber-700' : 'bg-amber-400/15 text-amber-300'}`} data-id="element-2201" data-mp="ejq1il1sdr47">
            {isCommand ? <User className="w-3.5 h-3.5" data-id="element-2202" data-mp="4l0g4iaoloj7" /> : <Bot className="w-4 h-4" data-id="element-2203" data-mp="4nmpmnh4ue42" />}
          </div>
        </div>

        <div className={`flex flex-col max-w-[68%] ${isCommand ? 'items-end' : 'items-start'}`} data-id="element-2204" data-mp="sbwgi3t7ds22">
          {/* Speaker name label */}
          <span className={`flex items-center gap-1 text-[10px] font-semibold mb-1 px-0.5 ${isLight ? 'text-amber-700/80' : 'text-amber-300/80'}`} data-id="element-2205" data-mp="jjcequhm77d0">
            {isCommand ? 'You' : 'Vexu'}
          </span>
          <div className={`rounded-[14px] px-3 py-2 ${isCommand ? isLight ? 'bg-amber-100 text-amber-900 rounded-tr-[4px] border border-amber-200/80' : 'bg-amber-400/[0.16] text-amber-50 rounded-tr-[4px] border border-amber-400/20' : isLight ? 'bg-amber-50 text-amber-900/90 rounded-tl-[4px] border border-amber-200/60' : 'bg-amber-400/[0.08] text-amber-100/90 rounded-tl-[4px] border border-amber-400/[0.12]'}`} data-id="element-2206" data-mp="1b9758uh7l339">
            <p className="text-[12px] leading-[1.45] tracking-[-0.01em]" data-id="element-2207" data-mp="3airvt56h44ix">
              {m.text}
            </p>
            <div className={`flex items-center justify-end gap-1 mt-0.5 ${isLight ? 'text-amber-600/60' : 'text-amber-300/50'}`} data-id="element-2208" data-mp="2hs04al0cs41x">
              <span className="text-[9px] tracking-wide" data-id="element-2209" data-mp="2cuzxg7voix9r">{m.time}</span>
            </div>
          </div>
        </div>
      </motion.div>;
  };
  const openCallDetail = (call: Call) => {
    liveSimActiveRef.current = false;
    if (liveSimTimerRef.current) {
      clearTimeout(liveSimTimerRef.current);
      liveSimTimerRef.current = null;
    }
    setSelectedCall(call);
    setPlayingIdx(null);
    setCommandInput('');
    setLiveTyping(false);
  };
  const handleSelectCall = (call: Call) => {
    // A list row always opens the exact conversation the user tapped.
    // Repeat-caller history remains available inside that transcript.
    setSelectedCaller(null);
    openCallDetail(call);
  };
  const handleBack = () => {
    liveSimActiveRef.current = false;
    if (liveSimTimerRef.current) {
      clearTimeout(liveSimTimerRef.current);
      liveSimTimerRef.current = null;
    }
    setLiveTyping(false);
    // Return from a transcript to its caller history before leaving Calls.
    if (selectedCaller) {
      setSelectedCall(null);
      return;
    }
    // Show backup prompt on first back press
    if (!backupPromptShown) {
      setShowBackupPrompt(true);
      setBackupStep('ask');
      return;
    }
    setSelectedCall(null);
  };
  const handleCallerBack = () => {
    // Caller history was opened from a transcript: return to that exact call.
    if (callerOverviewCall) {
      openCallDetail(callerOverviewCall);
      setSelectedCaller(null);
      setCallerOverviewCall(null);
      return;
    }
    setSelectedCaller(null);
    setPlayingIdx(null);
  };
  const handleBackupEnable = () => {
    setBackupStep('provider');
  };
  const handleBackupProviderSelect = (provider: string) => {
    setBackupProvider(provider);
    setBackupStep('frequency');
  };
  const handleBackupFrequencySelect = (freq: string) => {
    setBackupFrequency(freq);
    setBackupStep('done');
    setTimeout(() => {
      setShowBackupPrompt(false);
      setBackupPromptShown(true);
      setSelectedCall(null);
    }, 1500);
  };
  const handleBackupSkip = () => {
    setShowBackupPrompt(false);
    setBackupPromptShown(true);
    setSelectedCall(null);
  };
  const handleSendCommand = () => {
    if (!commandInput.trim() || !selectedCall) return;
    // Add the command as a private, internal owner→AI message.
    const newMsg: Message = {
      sender: 'user',
      text: commandInput.trim(),
      internal: true,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    setSelectedCall({
      ...selectedCall,
      messages: [...selectedCall.messages, newMsg]
    });
    setCommandInput('');
    // Simulate AI response after 1s (also internal — the caller never hears it)
    setTimeout(() => {
      setSelectedCall(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: [...prev.messages, {
            sender: 'ai',
            text: "Got it, I'll take care of that right away.",
            internal: true,
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })
          }]
        };
      });
    }, 1000);
  };
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-2210" data-mp="1ckdhm83k4vyo">
      {/* Call List View */}
      <AnimatePresence data-id="element-2211" data-mp="17mbzpqhvb2os">
        {!selectedCall && !selectedCaller && <motion.div key="list" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -40
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 flex flex-col" data-id="element-2212" data-mp="17o59hens2310">
            {/* Header */}
            <div className={`px-5 pt-6 pb-3 relative z-10 ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-2213" data-mp="30h37fr5m1nuu">
              <div className="flex items-center justify-between mb-3" data-id="element-2214" data-mp="34hwwwoypbapu">
                <h1 className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2215" data-mp="2qm1lf46mt1wh">
                  Calls
                </h1>
                <button data-tour="calls-caller-experience" onClick={() => onNavigate('callerExperience')} className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20 hover:bg-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20 hover:bg-vexu/20'}`} data-id="element-2216" data-mp="ou557h9ir3vd">
                  <Users className="w-3.5 h-3.5" data-id="element-2217" data-mp="id3mk44zbhxo" />
                  Caller Experience
                </button>
              </div>
              {/* Search Bar */}
              <div data-tour="calls-search" className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl transition-colors ${isLight ? 'bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]' : 'bg-[#141414] border border-white/[0.06]'}`} data-id="element-2218" data-mp="1b0hdnllzxj9v">
                <Search className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2219" data-mp="wbv0nqjud3lt" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search calls..." className={`flex-1 text-[13px] bg-transparent outline-none ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} data-id="element-2220" data-mp="1ttv4pfml95k7" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${isLight ? 'text-slate-400 hover:text-slate-600 bg-slate-100' : 'text-zinc-500 hover:text-zinc-300 bg-white/5'}`} data-id="element-2221" data-mp="2b9z9hiw2mojb">
                    Clear
                  </button>}
              </div>
            </div>

            {/* Full-width Call List */}
            <div className="flex-1 overflow-y-auto pb-24 relative z-10" data-id="element-2222" data-mp="2tzf9m53ugez7">
              {showEmptyState ? <CallsEmptyState onLoadSampleCalls={() => setCallsSeeded(true)} onOpenCallerExperience={() => onNavigate('callerExperience')} data-id="element-2223" data-mp="2yx75fa7m0l8o" /> : filteredCalls.length === 0 ? <div className="flex flex-col items-center justify-center py-16" data-id="element-2224" data-mp="3vrgiutqljssb">
                  <Search className={`w-8 h-8 mb-3 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2225" data-mp="e9x9r5qndcbw" />
                  <p className={`text-sm font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2226" data-mp="9bm313vnfhxw">
                    No calls found
                  </p>
                </div> : filteredCalls.map((call, idx) => <motion.button key={call.id} initial={call.id === 999 ? {
            opacity: 0,
            y: -20
          } : false} animate={call.id === 999 ? {
            opacity: 1,
            y: 0
          } : undefined} transition={call.id === 999 ? {
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94]
          } : undefined} style={call.id === 999 ? {
            willChange: 'transform, opacity'
          } : undefined} data-tour={idx === 0 ? 'calls-list-item' : undefined} onClick={() => handleSelectCall(call)} className={`relative w-full px-5 py-3.5 flex items-center gap-3.5 transition-colors ${call.isEmergency ? isLight ? 'bg-red-50/70 hover:bg-red-50 active:bg-red-100/70' : 'bg-red-500/[0.07] hover:bg-red-500/[0.11] active:bg-red-500/[0.14]' : isLight ? 'hover:bg-white/80 active:bg-white' : 'hover:bg-white/[0.02] active:bg-white/[0.04]'} ${idx < filteredCalls.length - 1 ? isLight ? 'border-b border-slate-100/80' : 'border-b border-white/[0.04]' : ''}`} data-id="element-2227" data-mp="3qsdcn5yvrgir" data-mpi={idx}>
                    {call.isEmergency && <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[3px] bg-red-500" data-id="element-2228" data-mp="26wqw0auhtgd9" />}
                    <div className="relative" data-id="element-2229" data-mp="1x7pe2f4e0own">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] flex-shrink-0 ${call.color}`} data-id="element-2230" data-mp="1x4kykllh26qo">
                        {call.initials}
                      </div>
                      {call.isLive && <div className="absolute -top-0.5 -right-0.5" data-id="element-2231" data-mp="1j98yrzsglq2w">
                          <span className={`inline-flex rounded-full h-3 w-3 border-2 ${call.isEmergency ? 'bg-red-500' : 'bg-vexu'} ${isLight ? 'border-slate-50' : 'border-[#0A0A0A]'}`} data-id="element-2232" data-mp="3uu0090mzmeip" />
                        </div>}
                    </div>
                    <div className="flex-1 text-left min-w-0" data-id="element-2233" data-mp="cmakv8iqx8wd">
                      <div className="flex items-center gap-2" data-id="element-2234" data-mp="3c51tn3ahso71">
                        <span className={`text-[14px] font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2235" data-mp="3h2bq32wau04d">
                          {call.name}
                        </span>
                        {call.isEmergency && <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${isLight ? 'bg-red-600 text-white' : 'bg-red-600/90 text-white'}`} data-id="element-2236" data-mp="2tdireyoale62">
                            <Siren className="h-2.5 w-2.5" data-id="element-2237" data-mp="1lsgnfiv9u4d7" />
                            Emergency
                          </span>}
                        {call.isLive && !call.isEmergency && <span className={`text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20'}`} data-id="element-2238" data-mp="1vqd56tiprxka">
                            Live
                          </span>}
                        {call.isFirstCall && <span className={`text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${isLight ? 'bg-slate-100 text-slate-500 border border-slate-200' : 'bg-white/5 text-zinc-400 border border-white/10'}`} data-id="element-2239" data-mp="2ehw52rhfyvi3">
                            First call
                          </span>}
                      </div>
                      <div className={`flex items-center gap-1.5 text-[12px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2240" data-mp="2y364ern19qce">
                        {call.isLive ? <>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${call.isEmergency ? 'bg-red-500' : 'bg-vexu'}`} data-id="element-2241" data-mp="2obrz238ehwbc" />
                            <span className={`font-medium ${call.isEmergency ? isLight ? 'text-red-600' : 'text-red-400' : isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2242" data-mp="2fdzgmq7du8jq">
                              {call.isEmergency ? `Urgent · ${call.duration}` : call.duration}
                            </span>
                          </> : <>
                            {call.type === 'incoming' && <PhoneIncoming className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2243" data-mp="2rnplmhtly55n" />}
                            {call.type === 'outgoing' && <PhoneOutgoing className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2244" data-mp="2mq5wunzaa9zg" />}
                            <span data-id="element-2245" data-mp="2dq133gn0ja3r">{call.group}</span>
                            <span className={`${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2246" data-mp="6yibzqxjo7x0">
                              ·
                            </span>
                            <span data-id="element-2247" data-mp="1wf45rwh5schz">{call.duration}</span>
                          </>}
                      </div>
                    </div>
                    <div className={`text-[11px] font-medium flex-shrink-0 ${call.isEmergency ? isLight ? 'text-red-600' : 'text-red-400' : call.isLive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2248" data-mp="1reg6knis0pco">
                      {call.time}
                    </div>
                  </motion.button>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Caller History Overview */}
      <AnimatePresence data-id="element-2249" data-mp="2k5hpmzplb51h">
        {selectedCaller && callerProfile && !selectedCall && <motion.div key="caller-history" initial={{
        opacity: 0,
        x: 32
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 32
      }} transition={{
        duration: 0.24
      }} className={`absolute inset-0 z-20 flex flex-col ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-2250" data-mp="uqa6mdwsul4q">
            <header className={`px-5 pt-6 pb-4 border-b ${isLight ? 'bg-white border-slate-200' : 'bg-[#141414] border-white/5'}`} data-id="element-2251" data-mp="1tf40k788u3vx">
              <div className="flex items-center gap-3" data-id="element-2252" data-mp="1ydoz827ukfi9">
                <button onClick={handleCallerBack} aria-label="Back to Calls" className={`p-1.5 -ml-1.5 rounded-full transition-colors ${isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/5'}`} data-id="element-2253" data-mp="22fa7fa9fs5m4">
                  <ArrowLeft className="w-5 h-5" data-id="element-2254" data-mp="2h4y7k85vsy89" />
                </button>
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${callerProfile.color}`} data-id="element-2255" data-mp="2h2vc38lymcw5">
                  {callerProfile.initials}
                </div>
                <div className="min-w-0 flex-1" data-id="element-2256" data-mp="5gtd2pokxcxo">
                  <h1 className={`text-[17px] font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2257" data-mp="5g1m5s5xg3dq">
                    {callerProfile.name}
                  </h1>
                  <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2258" data-mp="3rqxfle3iw4h3">
                    {getCallPhoneNumber(callerProfile)} · {callerProfile.group}
                  </p>
                </div>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-5 pb-10" data-id="element-2259" data-mp="ei12ei94b3fm">
              <section className={`rounded-2xl p-4 mb-6 ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-2260" data-mp="ehragp4iim7a">
                <div className="flex items-start justify-between gap-4" data-id="element-2261" data-mp="1755c30j5o5xq">
                  <div data-id="element-2262" data-mp="rofc9q8v8i2g">
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2263" data-mp="rn40b359o409">
                      Previous calls
                    </p>
                    <div className="flex items-baseline gap-1.5 mt-1" data-id="element-2264" data-mp="mplwtsa0qgzn">
                      <span className={`text-3xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2265" data-mp="mron8df32c4w">
                        {callerHistoryCalls.length}
                      </span>
                      <span className={`text-sm font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2266" data-mp="10q56f3u4tv3x">
                        {callerHistoryCalls.length === 1 ? 'call' : 'calls'}
                      </span>
                    </div>
                    <p className={`text-[12px] mt-1.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2267" data-mp="1y5ao2vub2269">
                      {callerHistoryCalls.length === 0 ? 'This is their first call' : `${callerHistoryCalls.length} earlier ${callerHistoryCalls.length === 1 ? 'conversation' : 'conversations'}`}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-2268" data-mp="3nmelij2wvn96">
                    <Phone className="w-5 h-5" data-id="element-2269" data-mp="2uys5ucgess5y" />
                  </div>
                </div>
                <div className={`mt-4 pt-3 border-t flex items-center gap-2 text-[11px] ${isLight ? 'border-slate-100 text-slate-500' : 'border-white/5 text-zinc-400'}`} data-id="element-2270" data-mp="38xrsxfo9wz00">
                  <Clock3 className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2271" data-mp="o56hpirdewkz" />
                  {callerHistoryCalls.length > 0 ? `Most recent prior call: ${callerHistoryCalls[0]?.time}` : `Current call: ${callerOverviewCall?.time}`}
                </div>
              </section>

              <div className="flex items-center justify-between mb-2.5" data-id="element-2272" data-mp="3hx69m7orhp5q">
                <h2 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2273" data-mp="3myvltqnq08ve">
                  Previous calls
                </h2>
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2274" data-mp="2u9y2rlboq81l">
                  Newest first
                </span>
              </div>

              {callerHistoryCalls.length > 0 ? <>
                  <div className={`rounded-2xl overflow-hidden ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-2275" data-mp="38136ozm35mhq">
                    {callerHistoryCalls.map((call, index) => {
                const DirectionIcon = call.type === 'outgoing' ? PhoneOutgoing : PhoneIncoming;
                return <button key={call.id} onClick={() => openCallDetail(call)} aria-label={`Open call from ${call.time}, ${call.duration}. Summary: ${call.summary}`} className={`w-full text-left px-4 py-4 transition-colors ${isLight ? 'hover:bg-slate-50 active:bg-slate-100' : 'hover:bg-white/[0.03] active:bg-white/[0.06]'} ${index < callerHistoryCalls.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-2276" data-mp="232y6f469zrx3" data-mpi={index}>
                          <div className="flex items-start gap-3" data-id="element-2277" data-mp="1bb0elo02wgmt">
                            <div className={`w-8 h-8 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/5 text-zinc-400'}`} data-id="element-2278" data-mp="1b8nsqzr79m4h">
                              <DirectionIcon className="w-3.5 h-3.5" data-id="element-2279" data-mp="2paac476b67rf" />
                            </div>
                            <div className="min-w-0 flex-1" data-id="element-2280" data-mp="2u6sc6tqp1hxf">
                              <div className="flex items-center justify-between gap-2" data-id="element-2281" data-mp="2g8u1bybl03mo">
                                <p className={`text-[13px] font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2282" data-mp="2l75b691vme4w">
                                  {call.time}
                                </p>
                                <span className={`text-[11px] flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2283" data-mp="2bbanv0mpjuvs">
                                  {call.duration}
                                </span>
                              </div>
                              <div className={`flex items-center gap-1.5 mt-1 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2284" data-mp="1siqakrjhryd3">
                                <Phone className="w-3 h-3 flex-shrink-0" data-id="element-2285" data-mp="18yrdgmrp637s" />
                                <span className="text-[11px] font-medium" data-id="element-2286" data-mp="13zo9z6rd851x">
                                  {getCallPhoneNumber(call)}
                                </span>
                              </div>
                              <p className={`text-[12px] leading-relaxed mt-1 pr-2 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2287" data-mp="1dy4139qe7ebf">
                                {call.summary}
                              </p>
                            </div>
                            <ChevronRight className={`w-4 h-4 mt-1 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2288" data-mp="21ng6rzmih48z" />
                          </div>
                        </button>;
              })}
                  </div>
                  <div className={`flex items-center gap-2 mt-4 px-1 text-[11px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2289" data-mp="37z52dlfg0m95">
                    <FileText className="w-3.5 h-3.5" data-id="element-2290" data-mp="32yhdp6eoshi3" />
                    Tap any call to view its full transcript and summary.
                  </div>
                </> : <div className={`rounded-2xl p-5 text-center ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-2291" data-mp="2u1ovy9x5cshe">
                  <div className={`w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-500'}`} data-id="element-2292" data-mp="2k7pw5hkjpksm">
                    <FileText className="w-5 h-5" data-id="element-2293" data-mp="2a9sc769pya5t" />
                  </div>
                  <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2294" data-mp="2698b5rm0ymee">
                    No previous calls
                  </p>
                  <p className={`text-[12px] leading-relaxed mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2295" data-mp="gsmmg0h9p80a">
                    This is {callerProfile.name}'s first call with you.
                  </p>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Call Detail View */}
      <AnimatePresence data-id="element-2296" data-mp="3u9dj6md1t3g7">
        {selectedCall && <motion.div key="detail" initial={{
        opacity: 0,
        x: 40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 40
      }} transition={{
        duration: 0.25
      }} className={`absolute inset-0 z-20 flex flex-col ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-2297" data-mp="23i0q9b9klnf">
            {/* WhatsApp Header */}
            <div className={`px-4 pt-6 pb-3 flex items-center gap-3 ${isLight ? 'bg-white border-b border-slate-200' : 'bg-[#141414] border-b border-white/5'}`} data-id="element-2298" data-mp="mne6t2pd5vr6">
              <button onClick={() => handleBack()} className={`p-1 rounded-full ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2299" data-mp="vgzmqpd9lj26">
                <ArrowLeft className="w-5 h-5" data-id="element-2300" data-mp="3pabbb2efv7u" />
              </button>
              {!selectedCall.isLive ? <button type="button" onClick={() => {
            setSelectedCaller(selectedCall.name);
            setCallerOverviewCall(selectedCall);
            setSelectedCall(null);
          }} aria-label={`View ${selectedCall.name}'s call history`} className={`flex items-center gap-3 flex-1 min-w-0 -my-1.5 -ml-1.5 p-1.5 rounded-xl text-left transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-2301" data-mp="3uwmrpnvgk4mp">
                  <div className="relative" data-id="element-2302" data-mp="1rm1coldq7gwe">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selectedCall.color}`} data-id="element-2303" data-mp="1vpft3d5ktzad">
                      <span className="text-xs font-bold" data-id="element-2304" data-mp="1mo84xnwryeu0">
                        {selectedCall.initials}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0" data-id="element-2305" data-mp="2afx15fcpvng8">
                    <div className="flex items-center gap-1.5" data-id="element-2306" data-mp="2fd6o8ocmvn0w">
                      <h2 className={`text-sm font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2307" data-mp="2feq96s98im90">
                        {selectedCall.name}
                      </h2>
                      <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2308" data-mp="2tcerbsbx3fad" />
                    </div>
                    <p className={`text-[11px] ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2309" data-mp="19i3mm7xpzfez">
                      {selectedCall.group} · {selectedCall.time}
                    </p>
                  </div>
                </button> : <>
                  <div className="relative" data-id="element-2310" data-mp="viufdxrhrvn5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selectedCall.color}`} data-id="element-2311" data-mp="vk5kq20l5bgd">
                      <span className="text-xs font-bold" data-id="element-2312" data-mp="1o6zykg2wdckv">
                        {selectedCall.initials}
                      </span>
                    </div>
                    {selectedCall.isLive && <div className="absolute -top-0.5 -right-0.5" data-id="element-2313" data-mp="1egew2hk3rnwo">
                        <span className={`inline-flex rounded-full h-2.5 w-2.5 border-2 border-white dark:border-[#141414] ${selectedCall.isEmergency ? 'bg-red-500' : 'bg-vexu'}`} data-id="element-2314" data-mp="2bwucz22v7978" />
                      </div>}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2315" data-mp="2jgd3dtefu4pu">
                    <div className="flex items-center gap-2" data-id="element-2316" data-mp="2oeocu30tmd7j">
                      <h2 className={`text-sm font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2317" data-mp="2odd7vzvn2zeu">
                        {selectedCall.name}
                      </h2>
                      {selectedCall.isEmergency ? <span className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white" data-id="element-2318" data-mp="32d4rqh8u7a0r">
                          <Siren className="h-2.5 w-2.5" data-id="element-2319" data-mp="3c4scy0esvuts" />
                          Emergency
                        </span> : selectedCall.isLive && <span className={`text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-md ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20'}`} data-id="element-2320" data-mp="3l45qb461p3js">
                            Live
                          </span>}
                    </div>
                    <p className={`text-[11px] ${selectedCall.isEmergency ? isLight ? 'font-semibold text-red-600' : 'font-semibold text-red-400' : selectedCall.isLive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2321" data-mp="1q0zb885dlqv3">
                      {selectedCall.isLive ? `Live · ${selectedCall.duration}` : `${selectedCall.group} · ${selectedCall.time}`}
                    </p>
                  </div>
                </>}
              <div className="flex items-center gap-1" data-id="element-2322" data-mp="fc8raz1qzks2">
                <button onClick={() => setShowCallConfirm(true)} className={`p-2 rounded-full ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/5'}`} data-id="element-2323" data-mp="fdjxt6mkv6mv">
                  <Phone className="w-4 h-4" data-id="element-2324" data-mp="3rnmsfyuyckk9" />
                </button>
                {!selectedCall.isLive && <button onClick={() => setShowDeleteConfirm(true)} className={`p-2 rounded-full ${isLight ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/10'}`} data-id="element-2325" data-mp="gabnkxze94v">
                    <Trash2 className="w-4 h-4" data-id="element-2326" data-mp="3mpld9p0nd5e2" />
                  </button>}
              </div>
            </div>

            {/* Live Call Banner */}
            {selectedCall.isLive && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} className={`px-4 py-2.5 flex items-center justify-between ${selectedCall.isEmergency ? isLight ? 'bg-red-50 border-b border-red-200' : 'bg-red-500/[0.09] border-b border-red-500/20' : isLight ? 'bg-vexu/5 border-b border-vexu/10' : 'bg-vexu/[0.03] border-b border-vexu/10'}`} data-id="element-2327" data-mp="3dppvv2gi0e5l">
                <div className="flex items-center gap-2" data-id="element-2328" data-mp="26q2ou9ia1c2w">
                  <div className="flex items-center gap-1.5" data-id="element-2329" data-mp="26ngi2c37n0lq">
                    {selectedCall.isEmergency ? <motion.span animate={{
                opacity: [1, 0.3, 1]
              }} transition={{
                duration: 1.1,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="flex items-center gap-1.5" data-id="element-2330" data-mp="1wrd6kfmgxpm4">
                        <Siren className={`h-3 w-3 ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-2331" data-mp="3m3xe9wpc64it" />
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-2332" data-mp="13xgn2q20bzxo">
                          Emergency Call
                        </span>
                      </motion.span> : <>
                        <span className="w-1.5 h-1.5 rounded-full bg-vexu" data-id="element-2333" data-mp="1hq6g3efye54a" />
                        <span className={`text-[10px] font-semibold uppercase tracking-widest ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2334" data-mp="kzdwi6tt9uxr">
                          Active Call
                        </span>
                      </>}
                  </div>
                  <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2335" data-mp="yv7wg0clx9yq">
                    ·
                  </span>
                  <span className={`text-[10px] ${selectedCall.isEmergency ? isLight ? 'font-medium text-red-600/80' : 'font-medium text-red-400/80' : isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2336" data-mp="2aeu4vjzwvcml">
                    {selectedCall.isEmergency ? 'Escalating to you now' : 'AI Agent handling'}
                  </span>
                </div>
                <div className="flex items-center gap-0.5" data-id="element-2337" data-mp="1sumd7kzqjh23">
                  {[...Array(4)].map((_, i) => <motion.div key={i} animate={{
              height: ['3px', '10px', '5px', '8px', '3px']
            }} transition={{
              duration: selectedCall.isEmergency ? 0.9 : 1.2,
              repeat: Infinity,
              delay: i * 0.15
            }} className={`w-[2px] rounded-full ${selectedCall.isEmergency ? 'bg-red-500/70' : 'bg-vexu/60'}`} data-id="element-2338" data-mp="1xt5wfdmy2eqr" data-mpi={i} />)}
                </div>
              </motion.div>}

            {/* Scrollable Content */}
            <div ref={detailScrollRef} className={`relative flex-1 overflow-y-auto pb-20 transition-colors ${selectedCall.isEmergency ? isLight ? 'bg-red-50/40' : 'bg-red-500/[0.04]' : ''}`} data-id="element-2339" data-mp="12mos9wemypdx">

              {/* Date pill */}
              <div className="flex justify-center py-3" data-id="element-2340" data-mp="nwhdbqnu7ohz">
                <span className={`text-[10px] font-medium px-3 py-1 rounded-full ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/5 text-zinc-500'}`} data-id="element-2341" data-mp="juclmxgy91xz">
                  {selectedCall.isLive ? 'Now' : selectedCall.time === 'Yesterday' || selectedCall.time === 'Mon' || selectedCall.time === 'Sun' ? selectedCall.time : 'Today'}
                </span>
              </div>

              {/* Chat Thread */}
              <div className="px-5 space-y-1.5 pb-4" data-id="element-2342" data-mp="2ixgdjf2m1og">
                {selectedCall.messages.map((msg, idx) => {
              const isCaller = msg.sender === 'user';
              // Only show the speaker's avatar + name on the first message
              // of a consecutive run, keeping the thread clean.
              const isRunStart = idx === 0 || selectedCall.messages[idx - 1].sender !== msg.sender || selectedCall.messages[idx - 1].internal !== msg.internal;
              // Private owner↔AI side-channel: rendered as a centered
              // "internal note", clearly separate from the spoken call.
              // For COMPLETED calls these are relocated to below the call
              // summary (rendered separately), since the commands happen
              // after the call ended. During a LIVE call they stay inline.
              if (msg.internal) {
                if (!selectedCall.isLive) return null;
                const runStartInternal = idx === 0 || !selectedCall.messages[idx - 1].internal;
                return <div key={idx} data-id="element-2343" data-mp="1s3d1ngas90ot">
                        {runStartInternal && <div className="flex items-center gap-2 w-full pt-3 pb-1.5" data-id="element-2344" data-mp="1o4dr9g6a8bpz">
                            <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-2345" data-mp="10f2s234kypdy" />
                            <span className={`flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-amber-700/80' : 'text-amber-300/70'}`} data-id="element-2346" data-mp="196v1cahvbsgy">
                              <StickyNote className="w-2.5 h-2.5" data-id="element-2347" data-mp="gohd92mc3kv2" />
                              Private — not heard by caller
                            </span>
                            <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-2348" data-mp="pepnuyskprt8" />
                          </div>}
                        {renderInternalBubble(msg, idx)}
                      </div>;
              }
              return <motion.div key={idx} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.05,
                duration: 0.2
              }} className={`flex items-end gap-2 ${isCaller ? 'flex-row-reverse' : 'flex-row'} ${isRunStart ? 'pt-2.5' : ''}`} data-id="element-2349" data-mp="19274bik1cutd" data-mpi={idx}>
                      {/* Speaker avatar — shown once per run, spacer otherwise */}
                      <div className="w-7 flex-shrink-0 self-end" data-id="element-2350" data-mp="3cj3q4ugp4k13">
                        {isRunStart && (isCaller ? <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${selectedCall.color}`} data-id="element-2351" data-mp="37k8wexh6xb27">
                              {selectedCall.initials}
                            </div> : <div className="w-7 h-7 rounded-full bg-vexu flex items-center justify-center" data-id="element-2352" data-mp="3v7gyl6jbo400">
                              <Bot className="w-4 h-4 text-white" data-id="element-2353" data-mp="3lb5l4d7zx83z" />
                            </div>)}
                      </div>

                      <div className={`flex flex-col max-w-[68%] ${isCaller ? 'items-end' : 'items-start'}`} data-id="element-2354" data-mp="2ewmstp7yq6fc">
                        {/* Speaker name label */}
                        {isRunStart && <span className={`text-[10px] font-semibold mb-1 px-0.5 ${isCaller ? isLight ? 'text-slate-500' : 'text-zinc-400' : isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2355" data-mp="15xv7j104t0lw">
                            {isCaller ? selectedCall.name : 'Vexu AI'}
                          </span>}
                        <div className={`rounded-[14px] px-3 py-2 ${isCaller ? 'bg-vexu text-white rounded-tr-[4px]' : isLight ? 'bg-white text-slate-800 rounded-tl-[4px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-slate-100/80' : 'bg-[#1A1A1A] text-zinc-100 rounded-tl-[4px] border border-white/[0.04]'}`} data-id="element-2356" data-mp="i6poye2t3u7w">
                          <div className="flex items-center gap-2" data-id="element-2357" data-mp="3vgdpxykkhop3">
                            <button onClick={() => togglePlay(idx)} className={`flex-shrink-0 w-5.5 h-5.5 w-[22px] h-[22px] rounded-full flex items-center justify-center transition-colors ${playingIdx === idx ? msg.sender === 'user' ? 'bg-white/20 text-white' : 'bg-vexu text-white' : msg.sender === 'user' ? 'bg-white/10 text-white/70' : isLight ? 'bg-slate-50 text-slate-400 border border-slate-100' : 'bg-white/[0.06] text-zinc-500'}`} data-id="element-2358" data-mp="49ayr74qbiev">
                              {playingIdx === idx ? <div className="w-2 h-2 bg-current rounded-[2px]" data-id="element-2359" data-mp="3vpw5kimv9mr4" /> : <Play className="w-2.5 h-2.5 ml-[1px]" data-id="element-2360" data-mp="dbwts518i3e6" />}
                            </button>
                            <div className="flex-1 min-w-0 relative" data-id="element-2361" data-mp="8dby8iwxg7w2">
                              {/* Text always rendered to maintain bubble size */}
                              <motion.p animate={{
                          opacity: playingIdx === idx ? 0 : 1
                        }} transition={{
                          duration: 0.2
                        }} className="text-[12px] leading-[1.45] tracking-[-0.01em]" data-id="element-2362" data-mp="ibj5ga1ezbkv">
                                {msg.text}
                              </motion.p>
                              {/* Waveform overlay */}
                              <AnimatePresence data-id="element-2363" data-mp="2lc54ha9ga9u9">
                                {playingIdx === idx && <motion.div initial={{
                            opacity: 0
                          }} animate={{
                            opacity: 1
                          }} exit={{
                            opacity: 0
                          }} transition={{
                            duration: 0.2
                          }} className="absolute inset-0 flex items-center gap-[2px]" data-id="element-2364" data-mp="2qay6o6jaeef4">
                                    {[...Array(28)].map((_, i) => <motion.div key={i} animate={{
                              height: ['15%', '100%', '35%', '75%', '15%']
                            }} transition={{
                              duration: 0.5 + Math.random() * 0.5,
                              repeat: Infinity
                            }} className={`w-[2px] rounded-full ${msg.sender === 'user' ? 'bg-white/35' : 'bg-vexu/40'}`} data-id="element-2365" data-mp="rokqid5x888" data-mpi={i} />)}
                                  </motion.div>}
                              </AnimatePresence>
                            </div>
                          </div>
                          <div className={`flex items-center justify-end gap-1 mt-0.5 ${isCaller ? 'text-white/40' : isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2366" data-mp="1vc13sj8opjgu">
                            <span className="text-[9px] tracking-wide" data-id="element-2367" data-mp="1gofycioc785r">
                              {msg.time}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>;
            })}

                {/* Live typing indicator */}
                {selectedCall.isLive && liveTyping && <motion.div initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.15
            }} className="flex items-end gap-2" data-id="element-2368" data-mp="3edbd12lci1cw">
                    <div className="w-7 flex-shrink-0 self-end" data-id="element-2369" data-mp="5vnjdvlzi22b">
                      <div className="w-7 h-7 rounded-full bg-vexu flex items-center justify-center" data-id="element-2370" data-mp="2lo4e42hzb9r4">
                        <Bot className="w-4 h-4 text-white" data-id="element-2371" data-mp="2qmo0u54ecpkj" />
                      </div>
                    </div>
                    <div className={`rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5 ${isLight ? 'bg-white border border-slate-100/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]' : 'bg-[#1A1A1A] border border-white/[0.04]'}`} data-id="element-2372" data-mp="26l31jjoghr3m">
                      <div className="flex gap-[3px] items-center" data-id="element-2373" data-mp="1xq6ilzexp02k">
                        {[0, 0.15, 0.3].map((delay, i) => <motion.div key={i} animate={{
                    opacity: [0.3, 0.8, 0.3]
                  }} transition={{
                    duration: 0.9,
                    repeat: Infinity,
                    delay
                  }} className={`w-[5px] h-[5px] rounded-full ${isLight ? 'bg-slate-300' : 'bg-zinc-600'}`} data-id="element-2374" data-mp="1nuv4njbcnwvv" data-mpi={i} />)}
                      </div>
                    </div>
                  </motion.div>}
              </div>

              {/* AI Summary Card - only for completed calls */}
              {!selectedCall.isLive && <div className="px-4" data-id="element-2375" data-mp="3lhe3pwq2ort4">
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: selectedCall.messages.length * 0.07 + 0.2
            }} className={`overflow-hidden rounded-2xl ${selectedCall.isEmergency ? isLight ? 'bg-white border border-red-200 shadow-sm shadow-red-900/5' : 'bg-[#171112] border border-red-500/25' : isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-2376" data-mp="3lifm6m9f4gkv">
                    {selectedCall.isEmergency && <div className={`flex items-center gap-2 px-4 py-2 ${isLight ? 'bg-red-600' : 'bg-red-600/90'}`} data-id="element-2377" data-mp="kktwlb48zf14">
                        <Siren className="h-3.5 w-3.5 flex-shrink-0 text-white" data-id="element-2378" data-mp="ujt58tp0jao4" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-2379" data-mp="uhgd5npjjjxc">
                          Emergency call
                        </span>
                      </div>}
                    <div className="p-5" data-id="element-2380" data-mp="ylo2nn3tp3wm">
                    <div className="flex items-center gap-2 mb-3" data-id="element-2381" data-mp="ykmt5n2ggph0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${selectedCall.isEmergency ? isLight ? 'bg-red-100' : 'bg-red-500/15' : 'bg-vexu/15'}`} data-id="element-2382" data-mp="1dau0p7qxco2x">
                        {selectedCall.isEmergency ? <Siren className={`w-3.5 h-3.5 ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-2383" data-mp="1i9nhpa5f6x7u" /> : <CheckCircle2 className="w-3.5 h-3.5 text-vexu" data-id="element-2384" data-mp="25wscvza6bd8j" />}
                      </div>
                      <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2385" data-mp="1rxsomsqkp0cw">
                        Call Summary
                      </h3>
                    </div>
                    <div className={`flex items-center gap-4 mb-3 pb-3 border-b ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-2386" data-mp="1i5velpbawefr">
                      <div data-id="element-2387" data-mp="197t3ui1rm7fa">
                        <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2388" data-mp="194f47hkhkcbj">
                          Duration
                        </p>
                        <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2389" data-mp="147oqv202futt">
                          {selectedCall.duration}
                        </p>
                      </div>
                      <div data-id="element-2390" data-mp="2x4afm5vylnyo">
                        <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2391" data-mp="2x3hi7viw4at7">
                          Group
                        </p>
                        <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2392" data-mp="3otcign8adkwn">
                          {selectedCall.group}
                        </p>
                      </div>
                    </div>
                    <p className={`text-xs leading-relaxed ${selectedCall.booking ? 'mb-3' : 'mb-2'} ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2393" data-mp="3av5qfnq0qf4w">
                      {selectedCall.summary}
                    </p>
                    {selectedCall.booking && <button type="button" onClick={() => onViewBookedEvent?.(selectedCall.booking!.eventId)} aria-label={`Open booked meeting: ${selectedCall.booking.title}`} className={`mb-3 w-full rounded-xl border px-2.5 py-2 text-left transition-all active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'border-vexu/20 bg-vexu/[0.05] hover:bg-vexu/[0.09]' : 'border-vexu/20 bg-vexu/[0.09] hover:bg-vexu/[0.14]'}`} data-id="element-2394" data-mp="1pah7mrt73kow">
                        <span className="flex items-center gap-2.5" data-id="element-2395" data-mp="2i20vip0qlmx8">
                          <span className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg ${isLight ? 'bg-white text-vexu-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)]' : 'bg-vexu/15 text-vexu'}`} data-id="element-2396" data-mp="2mzaw90tktl0d"><CalendarDays className="h-3.5 w-3.5" data-id="element-2397" data-mp="slt2gw2kvrww" /></span>
                          <span className="min-w-0 flex-1" data-id="element-2398" data-mp="so5tdyqbdcm3">
                            <span className={`block truncate text-[13px] font-semibold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2399" data-mp="2uik1qiyyozk5">{selectedCall.booking.title}</span>
                            <span className={`mt-0.5 block truncate text-[10.5px] leading-tight ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2400" data-mp="348mtkfuck7gw"><span className={`font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2401" data-mp="2zb3i1np1a8sn">Booked</span> · {selectedCall.booking.time}</span>
                          </span>
                          <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 ${isLight ? 'text-vexu-700/70' : 'text-vexu/70'}`} data-id="element-2402" data-mp="26mxtepuuzznj" />
                        </span>
                      </button>}
                    <button type="button" onClick={() => {
                  setSelectedCaller(selectedCall.name);
                  setCallerOverviewCall(selectedCall);
                  setSelectedCall(null);
                }} className={`w-full flex items-center justify-between gap-3 mb-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isLight ? 'bg-vexu/5 border border-vexu/15 hover:bg-vexu/10' : 'bg-vexu/[0.07] border border-vexu/15 hover:bg-vexu/10'}`} aria-label={`View ${selectedCall.name}'s call history`} data-id="element-2403" data-mp="2giisaf4msjt2">
                      <span className={`text-[11px] leading-snug ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-2404" data-mp="m3bxw91urtj4">
                        <span className="font-semibold" data-id="element-2405" data-mp="r519bpszm97q">
                          {selectedCallHistoryCount > 0 ? `${selectedCall.name} has ${selectedCallHistoryCount} previous ${selectedCallHistoryCount === 1 ? 'call' : 'calls'}` : `This is ${selectedCall.name}'s first call`}
                        </span>{' '}
                        in your call history.
                      </span>
                      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2406" data-mp="35icwo3n12zpu" />
                    </button>
                    {/* Play Full Call Button */}
                    <button onClick={() => setPlayingIdx(playingIdx === -1 ? null : -1)} className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all ${playingIdx === -1 ? isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20' : isLight ? 'bg-slate-50 border border-slate-100 hover:bg-slate-100/80' : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-2407" data-mp="2wjrislsi48ve">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${playingIdx === -1 ? 'bg-vexu text-white' : isLight ? 'bg-white text-slate-500 shadow-sm border border-slate-100' : 'bg-white/10 text-zinc-400'}`} data-id="element-2408" data-mp="23tiwqiuuh4bj">
                        {playingIdx === -1 ? <Pause className="w-3.5 h-3.5" data-id="element-2409" data-mp="1wbuiohgh8sle" /> : <Play className="w-3.5 h-3.5 ml-0.5" data-id="element-2410" data-mp="2a8q479dx0cic" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2411" data-mp="258lmjg19tbqj">
                        {playingIdx === -1 ? <div className="flex items-center gap-[2px] h-5" data-id="element-2412" data-mp="1hihgz34iv1ww">
                            {[...Array(32)].map((_, i) => <motion.div key={i} animate={{
                        height: ['15%', '100%', '35%', '75%', '15%']
                      }} transition={{
                        duration: 0.5 + Math.random() * 0.5,
                        repeat: Infinity
                      }} className="w-[2px] rounded-full bg-vexu/50" data-id="element-2413" data-mp="1rawyy0ftjqhs" data-mpi={i} />)}
                          </div> : <span className={`text-[12px] font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2414" data-mp="33pf0ivkc404q">
                            Play Full Call
                          </span>}
                      </div>
                      <span className={`text-[10px] font-medium ${playingIdx === -1 ? 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2415" data-mp="2fzu4baz676di">
                        {selectedCall.duration}
                      </span>
                    </button>
                    </div>
                  </motion.div>

                  {/* Post-call commands to the AI — logically after the summary */}
                  {selectedCall.messages.some(m => m.internal) && <div className="mt-4" data-id="element-2416" data-mp="1komgwpztyy7m">
                      <div className="flex items-center gap-2 mb-2" data-id="element-2417" data-mp="1kpp8l04z94ol">
                        <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-2418" data-mp="1fxnzfn33vu2s" />
                        <span className={`flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-amber-700/80' : 'text-amber-300/70'}`} data-id="element-2419" data-mp="1aykue2n4lnut">
                          <StickyNote className="w-2.5 h-2.5" data-id="element-2420" data-mp="wz1z4afey6zx" />
                          Your commands · private
                        </span>
                        <span className={`flex-1 h-px ${isLight ? 'bg-amber-200/70' : 'bg-amber-400/15'}`} data-id="element-2421" data-mp="s1a5nbz4ectm" />
                      </div>
                      <div className="space-y-1.5" data-id="element-2422" data-mp="2mdq5nv1bouq1">
                        {selectedCall.messages.filter(m => m.internal).map((m, i) => renderInternalBubble(m, i))}
                      </div>
                    </div>}
                </div>}
            </div>

            {/* Floating Command Input Bar */}
            <div className="absolute bottom-5 left-4 right-4 z-30" data-id="element-2423" data-mp="1f0phd2t6zg6w">
              <div className={`flex gap-2 p-2 rounded-full shadow-lg backdrop-blur-xl ${isLight ? 'bg-white/80 border border-slate-200/60 shadow-slate-200/50' : 'bg-[#141414]/80 border border-white/10 shadow-black/40'}`} data-id="element-2424" data-mp="10ckzg11yr8p6">
                <input type="text" value={commandInput} onChange={e => setCommandInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSendCommand()} placeholder="Send a command to agent..." className={`flex-1 rounded-full px-4 py-2 text-sm outline-none bg-transparent ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} data-id="element-2425" data-mp="rd7p7660mg3e" />
                <button onClick={handleSendCommand} disabled={!commandInput.trim()} className="w-9 h-9 rounded-full bg-vexu text-white flex items-center justify-center disabled:opacity-40 transition-all flex-shrink-0" data-id="element-2426" data-mp="3tz5olpgu8tw5">
                  <Send className="w-4 h-4" data-id="element-2427" data-mp="lob6ye11pkn1" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Call Confirmation Modal */}
      <AnimatePresence data-id="element-2428" data-mp="vf5tzkm48o9j">
        {showCallConfirm && selectedCall && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2429" data-mp="qgcghs6twx9d">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCallConfirm(false)} data-id="element-2430" data-mp="1cgth0954zhmf" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2431" data-mp="1heuyipmwz8vs">
              <div className="p-6 space-y-5" data-id="element-2432" data-mp="5uzbsfnxgni2">
                <div className="flex items-start justify-between" data-id="element-2433" data-mp="asr1r450hzjm">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2434" data-mp="2o1qv6ajoqgom">
                    <Phone className={`w-6 h-6 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2435" data-mp="2j964hhw2sjpb" />
                  </div>
                  <button onClick={() => setShowCallConfirm(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-2436" data-mp="2abndzxznxd61">
                    <X className="w-4 h-4" data-id="element-2437" data-mp="32xf8cp6zee4n" />
                  </button>
                </div>

                <div data-id="element-2438" data-mp="2y29jpim72lcv">
                  <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2439" data-mp="2t1c5fh4b2bgn">
                    Call {selectedCall.name}?
                  </h3>
                  <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2440" data-mp="1hldo6pyz4j4c">
                    This will open your phone dialer to call this contact
                    directly.
                  </p>
                </div>

                {/* Contact info card */}
                <div className={`flex items-center gap-3 p-3.5 rounded-xl ${isLight ? 'bg-slate-50 border border-slate-100' : 'bg-white/[0.03] border border-white/[0.06]'}`} data-id="element-2441" data-mp="37rjpsq0grqrw">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0 ${selectedCall.color}`} data-id="element-2442" data-mp="4a1rb96kzw45">
                    {selectedCall.initials}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2443" data-mp="3vn60gps1h2ro">
                    <p className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2444" data-mp="3vll2srafh3pl">
                      {selectedCall.name}
                    </p>
                    <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2445" data-mp="2cozyxds1xyr1">
                      {selectedCall.group}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-1" data-id="element-2446" data-mp="2me9mqhfv4v9c">
                  <button onClick={() => setShowCallConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-2447" data-mp="2hfgd503p5oeg">
                    Cancel
                  </button>
                  <button onClick={() => {
                setShowCallConfirm(false);
                window.open('tel:+15550000000', '_self');
              }} className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-2448" data-mp="22oqy1mn47p5b">
                    <Phone className="w-4 h-4" data-id="element-2449" data-mp="3op59t4sjhv0w" />
                    Call Now
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Delete Call Confirmation Modal */}
      <AnimatePresence data-id="element-2450" data-mp="39v94zfvlfkif">
        {showDeleteConfirm && selectedCall && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2451" data-mp="bcv9u91yetd3">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} data-id="element-2452" data-mp="u2tcg9mzfu9c" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2453" data-mp="pajtxeo75wh3">
              <div className="p-6 space-y-5" data-id="element-2454" data-mp="1kj3t5ucim55i">
                <div className="flex items-start justify-between" data-id="element-2455" data-mp="1pbn8owurjacy">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-red-50' : 'bg-red-500/10'}`} data-id="element-2456" data-mp="1ub0445zp9pr5">
                    <Trash2 className={`w-6 h-6 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-2457" data-mp="1z8001o2u5c4j" />
                  </div>
                  <button onClick={() => setShowDeleteConfirm(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-2458" data-mp="1em3f1rd7x5uz">
                    <X className="w-4 h-4" data-id="element-2459" data-mp="1ocod9dozc8dd" />
                  </button>
                </div>

                <div data-id="element-2460" data-mp="1ji1zx4zc85qn">
                  <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2461" data-mp="1xb9r4vjiaz7t">
                    Delete this call?
                  </h3>
                  <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2462" data-mp="279fuzioviffb">
                    The conversation with{' '}
                    <span className={`font-semibold ${isLight ? 'text-slate-800' : 'text-white'}`} data-id="element-2463" data-mp="cvesmnu47aah">
                      {selectedCall.name}
                    </span>
                    , including the transcript and AI summary, will be
                    permanently removed. This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-3 pt-1" data-id="element-2464" data-mp="29uf44mwssnbw">
                  <button onClick={() => setShowDeleteConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-2465" data-mp="29tm5yabwn65d">
                    Cancel
                  </button>
                  <button onClick={handleDeleteCall} className="flex-1 py-3 rounded-xl font-medium text-sm bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center justify-center gap-2" data-id="element-2466" data-mp="1qw2wj5ljblif">
                    <Trash2 className="w-4 h-4" data-id="element-2467" data-mp="3q8dolvj90z1h" />
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Cloud Backup Prompt Overlay */}
      <AnimatePresence data-id="element-2468" data-mp="1l7kp5xiih2lf">
        {showBackupPrompt && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2469" data-mp="1h4y4qho66jy9">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleBackupSkip} data-id="element-2470" data-mp="12eqnu6dt98zo" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2471" data-mp="tgwp6oskq39c">
              <AnimatePresence mode="wait" data-id="element-2472" data-mp="394agjl07q2jp">
                {/* Step 1: Ask to enable backup */}
                {backupStep === 'ask' && <motion.div key="ask" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-5" data-id="element-2473" data-mp="2hdedfc3yfc9m">
                    <div className="flex items-start justify-between" data-id="element-2474" data-mp="30yq2e40uj22b">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2475" data-mp="2w0om1qut1guj">
                        <CloudUpload className={`w-6 h-6 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2476" data-mp="35r17dlag1e1q" />
                      </div>
                      <button onClick={handleBackupSkip} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-2477" data-mp="2d35b2hosdo5h">
                        <X className="w-4 h-4" data-id="element-2478" data-mp="2mz8errzaduuj" />
                      </button>
                    </div>

                    <div data-id="element-2479" data-mp="6acng7845lu7">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2480" data-mp="2akn2hsvilzx">
                        Back up your calls
                      </h3>
                      <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2481" data-mp="etg84g1k7svo">
                        Keep your call recordings, transcripts, and AI summaries
                        safe in the cloud. You can restore them anytime.
                      </p>
                    </div>

                    <div className={`flex items-center gap-3 p-3 rounded-xl ${isLight ? 'bg-slate-50' : 'bg-white/[0.03]'}`} data-id="element-2482" data-mp="smxj3cg6jxa3">
                      <Shield className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2483" data-mp="iu8g75yp3rqd" />
                      <p className={`text-[11px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2484" data-mp="1bixaom58wyt5">
                        Your data is encrypted end-to-end and stored securely on
                        your cloud account.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-1" data-id="element-2485" data-mp="37l0mdk0ea4f4">
                      <button onClick={handleBackupSkip} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-2486" data-mp="33kguovylbt0d">
                        Not now
                      </button>
                      <button onClick={handleBackupEnable} className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-2487" data-mp="3mbhpd7soh2e4">
                        Enable Backup
                      </button>
                    </div>
                  </motion.div>}

                {/* Step 2: Choose cloud provider */}
                {backupStep === 'provider' && <motion.div key="provider" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-4" data-id="element-2488" data-mp="1vr6uw6voczrf">
                    <div data-id="element-2489" data-mp="2oii3y6nj9mlx">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2490" data-mp="2og499ywrmcew">
                        Choose cloud account
                      </h3>
                      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2491" data-mp="z2imm29rgqv7">
                        Where should we store your call backups?
                      </p>
                    </div>

                    <div className="space-y-2.5" data-id="element-2492" data-mp="3ezax2o9imiyp">
                      {/* Google Drive */}
                      <button onClick={() => handleBackupProviderSelect('google')} className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border transition-all active:scale-[0.98] ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-2493" data-mp="3syahfjpzwbp2">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-100" data-id="element-2494" data-mp="gerkj2p57oy2">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-2495" data-mp="pfhb9rzjqp4x">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-2496" data-mp="kh62zlowgmot" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-2497" data-mp="1919enay1l2hp" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-2498" data-mp="lbezunu5x3ut" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-2499" data-mp="gd3q0d3vatcl" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left" data-id="element-2500" data-mp="292mk6ixh8rkb">
                          <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2501" data-mp="24bearmw5d675">
                            Google Drive
                          </p>
                          <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2502" data-mp="1vbr70ufy3oax">
                            alex.founder@gmail.com
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2503" data-mp="2iz95f8tptkmv" />
                      </button>

                      {/* iCloud */}
                      <button onClick={() => handleBackupProviderSelect('icloud')} className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border transition-all active:scale-[0.98] ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-2504" data-mp="17m6q0h3cr14a">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-b from-[#5AC8FA] to-[#007AFF] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-2505" data-mp="3n8j1mvj15pql">
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2506" data-mp="t6btkk5iwvn">
                            <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" data-id="element-2507" data-mp="2cktrzoolg248" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left" data-id="element-2508" data-mp="2gn6wk9rlttzf">
                          <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2509" data-mp="1l9w4srtni9yy">
                            iCloud
                          </p>
                          <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2510" data-mp="1v70qkqjl43f8">
                            alex@icloud.com
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2511" data-mp="xn7fvm685h8u" />
                      </button>
                    </div>

                    <button onClick={handleBackupSkip} className={`w-full py-2.5 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-2512" data-mp="17hzconrzg02k">
                      Skip for now
                    </button>
                  </motion.div>}

                {/* Step 3: Choose frequency */}
                {backupStep === 'frequency' && <motion.div key="frequency" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-4" data-id="element-2513" data-mp="2hfpcauqvk50g">
                    <div data-id="element-2514" data-mp="1osbopn92glt0">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2515" data-mp="1ov7lh896h4fv">
                        Backup frequency
                      </h3>
                      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2516" data-mp="3qik8ug599g6j">
                        How often should we back up your call data?
                      </p>
                    </div>

                    <div className="space-y-1.5" data-id="element-2517" data-mp="3hpqfxfwvyia7">
                      {[{
                  label: 'Daily',
                  desc: 'Every night at 2:00 AM',
                  value: 'daily'
                }, {
                  label: 'Weekly',
                  desc: 'Every Sunday at 2:00 AM',
                  value: 'weekly'
                }, {
                  label: 'Monthly',
                  desc: 'First day of each month',
                  value: 'monthly'
                }, {
                  label: 'Only when I tap "Back up"',
                  desc: 'Manual backups only',
                  value: 'manual'
                }].map(option => <button key={option.value} onClick={() => handleBackupFrequencySelect(option.value)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-colors ${isLight ? 'hover:bg-slate-50 active:bg-slate-100' : 'hover:bg-white/[0.03] active:bg-white/[0.06]'}`} data-id="element-2518" data-mp="2sornawvnq956">
                          <div data-id="element-2519" data-mp="29zl2937e55g7">
                            <p className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2520" data-mp="3gelyzb48prnu">
                              {option.label}
                            </p>
                            <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2521" data-mp="2x1o5kh8eq6m1">
                              {option.desc}
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`} data-id="element-2522" data-mp="31287dy3xdyei">
                            <div className="w-2 h-2 rounded-full" data-id="element-2523" data-mp="3as1fgp4uzve0" />
                          </div>
                        </button>)}
                    </div>

                    <button onClick={handleBackupSkip} className={`w-full py-2.5 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-2524" data-mp="3krsbwwczckgq">
                      Skip for now
                    </button>
                  </motion.div>}

                {/* Step 3: Done */}
                {backupStep === 'done' && <motion.div key="done" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="p-8 flex flex-col items-center text-center" data-id="element-2525" data-mp="16j7yd1a5e86l">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: 0.1
              }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2526" data-mp="2hxv6re76g2kx">
                      <CheckCircle2 className={`w-8 h-8 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2527" data-mp="79xg9f6asa3x" />
                    </motion.div>
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2528" data-mp="g6y0raq8xo23">
                      Backup enabled
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2529" data-mp="q4m60ulhadyi">
                      {backupFrequency === 'daily' && `Backed up daily to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'weekly' && `Backed up weekly to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'monthly' && `Backed up monthly to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'manual' && `Manual backup to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}